import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API_URL } from "../config";
import axios from "axios";
import { showToast } from "../components/toast";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import { hasPermission, getRoleDetails, getRoleUserid } from "../helpers/permission.js";

const AddOrderPopup = ({
  isOpen,
  onClose,
  onSubmit,
  dropdownOptions,
  subscriberState,
  subscriberGST,
  subscriberName,
  subscriberId,
}) => {
  const token = localStorage.getItem("authToken");
  const [loading, setLoading] = useState(false);
  const [pendingOrderList, setPendingOrderList] = useState([]);

  // ✅ state for API data
  const [templateOptions, setTemplateOptions] = useState({
    amountList: [],
    paymodeList: [],
    distributorList: [],
    stateList: [],
  });

  const [orderData, setOrderData] = useState({
    order_type: "1", // default to "New Order"
    order: "",
    pending_amount: "0",
    actual_amount: "",
    discount_amount: "0",
    paid_amount: "",
    payment_date: new Date(),
    order_due_date: null,
    payment_mode: "",
    payment_reference: "",
    billed_to: subscriberName || "",
    gst_no: subscriberGST || "",
    state: "",
    referralcode: "",
  });

  // ✅ fetch pending orders
  const fetchPendingOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}orders/pending-orders/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        setPendingOrderList(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching pending orders:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ fetch template (amount + payment modes)
  const fetchTemplateOptions = async () => {
    try {
      const response = await axios.get(`${API_URL}subscriber/get-template`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setTemplateOptions(
          response.data.data || { amountList: [], paymodeList: [] }
        );
      }
    } catch (error) {
      console.error("Error fetching template options:", error);
    }
  };

  useEffect(() => {
    if (subscriberId) {
      fetchPendingOrders();
    }
    // ✅ fetch template always when modal opens
    if (isOpen) {
      fetchTemplateOptions();
    }
  }, [subscriberId, isOpen]);

  useEffect(() => {
    if (isOpen) {
      const activeStateList =
        templateOptions.stateList?.length > 0
          ? templateOptions.stateList
          : dropdownOptions?.stateList || [];

      const matchedState = activeStateList.find(
        (option) =>
          String(option.stateName || option.state_name || "").toUpperCase() ===
            String(subscriberState || "").toUpperCase() ||
          String(option.stateCode) === String(subscriberState) ||
          String(option.state_code) === String(subscriberState) ||
          String(option.stateId) === String(subscriberState)
      );

      const resolvedState = matchedState
        ? (matchedState.stateCode !== undefined
            ? matchedState.stateCode
            : matchedState.state_code !== undefined
            ? matchedState.state_code
            : matchedState.stateId)
        : subscriberState || "";

      setOrderData((prev) => ({
        ...prev,
        billed_to: subscriberName || "",
        gst_no: subscriberGST || "",
        state: resolvedState || prev.state || "",
      }));
    }
  }, [
    isOpen,
    subscriberName,
    subscriberGST,
    subscriberState,
    dropdownOptions?.stateList,
    templateOptions?.stateList,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "order_type") {
      setOrderData((prev) => ({
        ...prev,
        order_type: value,
        actual_amount: value === "1" ? "" : prev.actual_amount,
        discount_amount: value === "1" ? "0" : prev.discount_amount,
        order: value === "2" ? "" : prev.order,
        pending_amount: value === "2" ? "0" : prev.pending_amount,
        paid_amount: value === "2" ? "" : prev.paid_amount,
      }));
    } else if (name === "order") {
      const selectedOrder = pendingOrderList.find((item) => item.id == value);
      const remainingAmount = selectedOrder?.remaining_amt || 0;

      setOrderData((prev) => ({
        ...prev,
        order: value,
        pending_amount: remainingAmount,
        paid_amount: remainingAmount,
        actual_amount: selectedOrder?.actual_amount || prev.actual_amount,
        order_due_date: selectedOrder?.order_due_date
          ? new Date(selectedOrder.order_due_date)
          : prev.order_due_date,
      }));
    } else if (name === "actual_amount") {
      setOrderData((prev) => {
        const actual = parseInt(value || 0);
        const discount = parseInt(prev.discount_amount || 0);
        return {
          ...prev,
          actual_amount: value,
          paid_amount: actual - discount >= 0 ? actual - discount : 0, // ✅ update paid amount
        };
      });
    } else if (name === "discount_amount") {
      setOrderData((prev) => {
        const discount = parseInt(value || 0);
        const actual = parseInt(prev.actual_amount || 0);
        return {
          ...prev,
          discount_amount: value,
          paid_amount: actual - discount >= 0 ? actual - discount : 0, // ✅ update paid amount
        };
      });
    } else {
      setOrderData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePaidAmountBlur = () => {
    const actualAmount = parseInt(orderData.actual_amount || 0);
    const discountAmount = parseInt(orderData.discount_amount || 0);
    const paidAmount = parseInt(orderData.paid_amount || 0);

    const calculatedAmount = actualAmount - discountAmount;

    if (calculatedAmount !== paidAmount) {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 10); // 10 days in future

      setOrderData((prev) => ({
        ...prev,
        order_due_date: futureDate,
      }));
    } else {
      setOrderData((prev) => ({
        ...prev,
        order_due_date: null,
      }));
    }
  };

  const handleDateChange = (name, date) => {
    setOrderData((prev) => ({ ...prev, [name]: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ❗ validations SAME as-is (unchanged)
    const actualAmount = parseInt(orderData.actual_amount || 0);
    const discountAmount = parseInt(orderData.discount_amount || 0);
    const paidAmount = parseInt(orderData.paid_amount || 0);

    const isFullyPaid = actualAmount - discountAmount === paidAmount;

    if (orderData.order_type == 1) {
      if (!orderData.actual_amount) {
        showToast.warning("Select Actual Amount");
        return;
      }
      if (!isFullyPaid && !orderData.order_due_date) {
        showToast.warning("Order due date is required");
        return;
      }
    }

    if (orderData.order_type == 2 && !orderData.order) {
      showToast.warning("Select Order");
      return;
    }

    if (!orderData.paid_amount) {
      showToast.warning("Paid Amount is required");
      return;
    }

    if (!orderData.payment_date) {
      showToast.warning("Payment date is required");
      return;
    }

    if (!orderData.payment_mode) {
      showToast.warning("select payment mode");
      return;
    }

    if (!orderData.payment_reference) {
      showToast.warning("Payment Reference is required");
      return;
    }

    if (!orderData.state) {
      showToast.warning("Select state");
      return;
    }

    // ✅ LOADING START
    setLoading(true);

    const success = await onSubmit(orderData);

    setLoading(false);
    // ✅ LOADING END

    if (success) {
      setOrderData({
        order_type: "1",
        order: "",
        pending_amount: "0",
        actual_amount: "",
        discount_amount: "0",
        paid_amount: "",
        payment_date: new Date(),
        order_due_date: null,
        payment_mode: "",
        payment_reference: "",
        billed_to: subscriberName || "",
        gst_no: subscriberGST || "",
        state:
          dropdownOptions.stateList?.find(
            (option) => option.stateName === subscriberState
          )?.stateCode || "",
        referralcode: "",
      });

      onClose(); // ✅ API success pachhi close
    }
  };


  if (!isOpen) return null;

  // if (loading) {
  //   return <LoadingSpinner />;
  // }

  const isDistributorVisible = () => {
    return hasPermission("subscriber_distributor", "view");
  };

  const isDistributorEditable = () => {
    return hasPermission("subscriber_distributor", "edit");
  };

  return (
    <div
      className="modal fade show"
      style={{ display: "block" }}
      tabIndex="-1"
      role="dialog"
      aria-labelledby="exampleModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog" style={{ maxWidth: "30rem" }}>
        <div className="modal-content">
          <div className="modal-header">
            <div className="col-md-11">
              <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                Place Order
              </h5>
            </div>
            <button type="button" className="btn btn-default" onClick={onClose}>
              &times;
            </button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="panel-body">
                <div className="row">
                  <table
                    className="table table-condensed table-hover bg-white usertable"
                    width="100%"
                  >
                    <tbody>
                      <tr>
                        <td colSpan="2">
                          <div className="d-flex ms-2 p-3">
                            <input
                              type="radio"
                              className="me-2"
                              name="order_type"
                              id="order_type1"
                              value="1"
                              checked={orderData.order_type === "1"}
                              onChange={handleChange}
                              style={{ width: "10%" }}
                            />
                            <label htmlFor="order_type1" className="m-0">
                              New Order
                            </label>

                            <input
                              type="radio"
                              className="me-2"
                              name="order_type"
                              id="order_type2"
                              value="2"
                              checked={orderData.order_type === "2"}
                              onChange={handleChange}
                              style={{ width: "10%" }}
                            />
                            <label htmlFor="order_type2" className="m-0">
                              Pending Order
                            </label>
                          </div>
                        </td>
                      </tr>

                      {orderData.order_type == 1 ? (
                        <>
                          <tr className="subscriber-block">
                            <td>
                              Actual Amount{" "}
                              <span style={{ color: "red" }}>*</span>
                            </td>
                            <td>
                              <select
                                name="actual_amount"
                                className="form-control"
                                value={orderData.actual_amount}
                                onChange={handleChange}
                              >
                                <option value="">- Select -</option>
                                {templateOptions?.amountList?.map(
                                  (option, index) => (
                                    <option key={index} value={option.value}>
                                      {option.label}
                                    </option>
                                  )
                                )}
                              </select>
                            </td>
                          </tr>

                          <tr className="subscriber-block">
                            <td>Discount Amount</td>
                            <td>
                              <input
                                type="text"
                                name="discount_amount"
                                value={orderData.discount_amount}
                                onChange={handleChange}
                                placeholder="Enter Discount Amount"
                                className="form-control"
                              />
                            </td>
                          </tr>
                        </>
                      ) : (
                        <>
                          <tr className="subscriber-block">
                            <td>
                              Order <span style={{ color: "red" }}>*</span>
                            </td>
                            <td>
                              <select
                                name="order"
                                className="form-control"
                                value={orderData.order}
                                onChange={handleChange}
                              >
                                <option value="">- Select -</option>
                                {pendingOrderList.map((option, index) => (
                                  <option key={index} value={option.id}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>

                          <tr className="subscriber-block">
                            <td>
                              Pending Amount{" "}
                              <span style={{ color: "red" }}>*</span>
                            </td>
                            <td>
                              <input
                                type="text"
                                name="pending_amount"
                                value={orderData.pending_amount}
                                onChange={handleChange}
                                placeholder="Enter Pending Amount"
                                className="form-control"
                                disabled
                              />
                            </td>
                          </tr>
                        </>
                      )}

                      <tr>
                        <td>
                          Paid Amount <span style={{ color: "red" }}>*</span>
                        </td>
                        <td>
                          <input
                            type="text"
                            name="paid_amount"
                            value={orderData.paid_amount}
                            onChange={handleChange}
                            onBlur={handlePaidAmountBlur}
                            placeholder="Enter Paid Amount"
                            className="form-control"
                          />
                        </td>
                      </tr>

                      <tr>
                        <td>
                          Payment Date <span style={{ color: "red" }}>*</span>
                        </td>
                        <td>
                          <DatePicker
                            selected={orderData.payment_date}
                            onChange={(date) =>
                              handleDateChange("payment_date", date)
                            }
                            dateFormat="dd-MM-yyyy"
                            className="form-control"
                            placeholderText="Select Payment Date"
                          />
                        </td>
                      </tr>

                      {orderData.order_type == 1 && (
                        <tr className="subscriber-block">
                          <td>Order Due Date</td>
                          <td>
                            <DatePicker
                              selected={orderData.order_due_date}
                              onChange={(date) =>
                                handleDateChange("order_due_date", date)
                              }
                              dateFormat="dd-MM-yyyy"
                              className="form-control"
                              placeholderText="Select Order Due Date"
                              minDate={new Date()} // disable past dates
                            />
                          </td>
                        </tr>
                      )}

                      <tr>
                        <td>
                          Payment Mode <span style={{ color: "red" }}>*</span>
                        </td>
                        <td>
                          <select
                            name="payment_mode"
                            className="form-control"
                            value={orderData.payment_mode}
                            onChange={handleChange}
                          >
                            <option value="">- Select -</option>
                            {templateOptions?.paymodeList?.map(
                              (option, index) => (
                                <option key={index} value={option.value}>
                                  {option.label}
                                </option>
                              )
                            )}
                          </select>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          Payment Reference no.{" "}
                          <span style={{ color: "red" }}>*</span>
                        </td>
                        <td>
                          <input
                            type="text"
                            name="payment_reference"
                            value={orderData.payment_reference}
                            onChange={handleChange}
                            placeholder="Enter Payment Reference"
                            className="form-control"
                          />
                        </td>
                      </tr>

                      <tr>
                        <td>Billed To</td>
                        <td>
                          <input
                            type="text"
                            name="billed_to"
                            value={orderData.billed_to}
                            onChange={handleChange}
                            placeholder="Enter Billed To"
                            className="form-control"
                          />
                        </td>
                      </tr>

                      <tr>
                        <td>GST No.</td>
                        <td>
                          <input
                            type="text"
                            name="gst_no"
                            value={orderData.gst_no}
                            onChange={handleChange}
                            placeholder="Enter GST No."
                            className="form-control"
                          />
                        </td>
                      </tr>

                      <tr>
                        <td>
                          State <span style={{ color: "red" }}>*</span>
                        </td>
                        <td>
                          <select
                            name="state"
                            className="form-control"
                            value={orderData.state}
                            onChange={handleChange}
                          >
                            <option value="">- Select -</option>
                            {(templateOptions.stateList?.length > 0
                              ? templateOptions.stateList
                              : dropdownOptions?.stateList || []
                            )?.map((option, index) => (
                              <option
                                key={index}
                                value={
                                  option.stateCode !== undefined
                                    ? option.stateCode
                                    : option.state_code !== undefined
                                    ? option.state_code
                                    : option.stateId
                                }
                              >
                                {option.stateName || option.state_name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>

                      {isDistributorVisible() && (
                        <tr>
                          <td>Distributor / Referral Code</td>
                          <td>
                            <select
                              name="referralcode"
                              className="form-control"
                              value={orderData.referralcode}
                              onChange={handleChange}
                              disabled={!isDistributorEditable()}
                            >
                              <option value="">- Select Distributor -</option>
                              {(templateOptions?.distributorList?.length > 0
                                ? templateOptions.distributorList
                                : dropdownOptions?.distributorList || []
                              )?.map((option, index) => (
                                <option
                                  key={index}
                                  value={option.referralcode || ""}
                                >
                                  {option.user_fullname}{" "}
                                  {option.referralcode
                                    ? `(${option.referralcode})`
                                    : ""}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div
                    className="col-md-12 mt-3"
                    style={{ textAlign: "right" }}
                  >
                    <button
                      type="submit"
                      name="submit"
                      className="btn btn-primary me-2"
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                    <button
                      type="button"
                      name="cancel"
                      className="btn btn-default"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOrderPopup;
